import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom'
import { useLogto } from '@logto/react'
import { Sidebar } from '@/components/layout/Sidebar'
import { PromptsPage } from '@/pages/PromptsPage'
import { ContentPage } from '@/pages/ContentPage'
import { LibraryPage } from '@/pages/LibraryPage'
import { SkillsPage } from '@/pages/SkillsPage'
import { CallbackPage } from '@/pages/CallbackPage'
import { LandingPage } from '@/pages/LandingPage'
import { BlogPage } from '@/pages/BlogPage'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { SetupPage } from '@/pages/SetupPage'
import { PricingPage } from '@/pages/PricingPage'
import AdminShell from '@/pages/admin/AdminShell'
import { Toast, SetupRequiredModal } from '@/components/common'
import { InsufficientCreditsModal } from '@/components/common/InsufficientCreditsModal'
import { useToast } from '@/hooks/useToast'
import { useAuthStore } from '@/store/useAuthStore'
import { useAppStore } from '@/store/useAppStore'
import { LogtoProviderWrapper, isLogtoEnabled } from '@/providers/LogtoProviderWrapper'
import { LogtoTokenSetup } from '@/components/auth/LogtoTokenSetup'

// ContentPage wrapper to handle route params
const ContentPageWrapper = () => {
  const { id } = useParams<{ id?: string }>()
  const libraryItemId = id ?? null
  return <ContentPage libraryItemId={libraryItemId} />
}

// Redirect to content studio if setup is done, otherwise prompts (which triggers setup modal)
const DefaultRedirect = () => {
  const companyInfo = useAppStore((state) => state.companyInfo)
  return <Navigate to={companyInfo ? '/app/content' : '/app/prompts'} replace />
}

// Pages that require company setup to be completed
const PAGES_REQUIRING_SETUP = ['/app/prompts', '/app/content', '/app/library']

// Logto-enabled app inner component
const LogtoAppInner = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { show, message } = useToast()
  const { user, logout, isLoading: isUserLoading } = useAuthStore()
  const companyInfo = useAppStore((state) => state.companyInfo)
  const [showSetupModal, setShowSetupModal] = useState(false)
  const [hasCheckedSetup, setHasCheckedSetup] = useState(false)
  const [hasFetchedData, setHasFetchedData] = useState(false)

  // Use Logto auth state directly
  const { isAuthenticated, isLoading, signOut } = useLogto()

  // Fetch user info (including company) when authenticated - only once
  useEffect(() => {
    if (isAuthenticated && !isLoading && !hasFetchedData) {
      setHasFetchedData(true)
      setHasCheckedSetup(false) // Reset while fetching
      // Get stable references from store
      const fetchCurrentUser = useAuthStore.getState().fetchCurrentUser
      const fetchLibrary = useAppStore.getState().fetchLibrary

      Promise.all([
        fetchCurrentUser(),
        fetchLibrary()
      ]).then(() => {
        setHasCheckedSetup(true)
      })
    } else if (!isAuthenticated && !isLoading) {
      setHasCheckedSetup(false)
      setHasFetchedData(false)
    }
  }, [isAuthenticated, isLoading, hasFetchedData])

  // Check if user needs to complete setup when accessing certain pages
  // Only check after data fetch is complete AND user data is not loading
  useEffect(() => {
    if (!isAuthenticated || !hasCheckedSetup || isUserLoading) return

    const currentPath = location.pathname
    const needsSetup = PAGES_REQUIRING_SETUP.some(
      (page) => currentPath === page || currentPath.startsWith(page + '/')
    )

    // Only show modal if we're certain user has no company (after fetch completed)
    // Also check if user object exists to ensure auth/me has returned
    if (needsSetup && !companyInfo && user) {
      setShowSetupModal(true)
    }
  }, [isAuthenticated, hasCheckedSetup, isUserLoading, location.pathname, companyInfo, user])

  // Handle setup modal confirmation
  const handleSetupConfirm = () => {
    setShowSetupModal(false)
    navigate('/app/setup')
  }

  // Handle logout - redirect to Logto for sign out
  const handleLogout = async () => {
    logout()
    // Logto signOut will redirect to Logto's sign-out page, then back to our app
    await signOut(window.location.origin + '/')
  }

  return <AppContent
    isAuthenticated={isAuthenticated}
    isLoading={isLoading}
    user={user}
    handleLogout={handleLogout}
    showSetupModal={showSetupModal}
    handleSetupConfirm={handleSetupConfirm}
    show={show}
    message={message}
  />
}

// Dev mode app inner component (no Logto)
const DevModeAppInner = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { show, message } = useToast()
  const { user, logout, isAuthenticated: storeAuthenticated, isLoading: isUserLoading } = useAuthStore()
  const companyInfo = useAppStore((state) => state.companyInfo)
  const [showSetupModal, setShowSetupModal] = useState(false)
  const [hasCheckedSetup, setHasCheckedSetup] = useState(false)
  const [hasFetchedData, setHasFetchedData] = useState(false)

  const isAuthenticated = storeAuthenticated
  const isLoading = false

  // Fetch user info (including company) when authenticated - only once
  useEffect(() => {
    if (isAuthenticated && !isLoading && !hasFetchedData) {
      setHasFetchedData(true)
      setHasCheckedSetup(false) // Reset while fetching
      // Get stable references from store
      const fetchCurrentUser = useAuthStore.getState().fetchCurrentUser
      const fetchLibrary = useAppStore.getState().fetchLibrary

      Promise.all([
        fetchCurrentUser(),
        fetchLibrary()
      ]).then(() => {
        setHasCheckedSetup(true)
      })
    } else if (!isAuthenticated) {
      setHasCheckedSetup(false)
      setHasFetchedData(false)
    }
  }, [isAuthenticated, isLoading, hasFetchedData])

  // Check if user needs to complete setup when accessing certain pages
  // Only check after data fetch is complete AND user data is not loading
  useEffect(() => {
    if (!isAuthenticated || !hasCheckedSetup || isUserLoading) return

    const currentPath = location.pathname
    const needsSetup = PAGES_REQUIRING_SETUP.some(
      (page) => currentPath === page || currentPath.startsWith(page + '/')
    )

    // Only show modal if we're certain user has no company (after fetch completed)
    // Also check if user object exists to ensure auth/me has returned
    if (needsSetup && !companyInfo && user) {
      setShowSetupModal(true)
    }
  }, [isAuthenticated, hasCheckedSetup, isUserLoading, location.pathname, companyInfo, user])

  // Handle setup modal confirmation
  const handleSetupConfirm = () => {
    setShowSetupModal(false)
    navigate('/app/setup')
  }

  // Handle logout
  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  return <AppContent
    isAuthenticated={isAuthenticated}
    isLoading={isLoading}
    user={user}
    handleLogout={handleLogout}
    showSetupModal={showSetupModal}
    handleSetupConfirm={handleSetupConfirm}
    show={show}
    message={message}
  />
}

// Shared app content component
interface AppContentProps {
  isAuthenticated: boolean
  isLoading: boolean
  user: any
  handleLogout: () => void
  showSetupModal: boolean
  handleSetupConfirm: () => void
  show: boolean
  message: string
}

const AppContent = ({
  isAuthenticated,
  isLoading,
  user,
  handleLogout,
  showSetupModal,
  handleSetupConfirm,
  show,
  message
}: AppContentProps) => {
  const navigate = useNavigate()

  // Register navigation globally for backward compatibility
  useEffect(() => {
    ;(window as any).navigateToPage = (page: string) => {
      navigate(`/app/${page}`)
    }

    ;(window as any).navigateToContentDetail = (libraryItemId: string) => {
      navigate(`/app/content/${libraryItemId}`)
    }
  }, [navigate])

  // SVG sprites are loaded globally by useSvgSprites() in AppRouter

  return (
    <div className="app active">
      {isAuthenticated && !isLoading && <Sidebar user={user} onLogout={handleLogout} />}
      <main className={isAuthenticated && !isLoading ? "main" : "main-full"}>
        <Routes>
          {/* OAuth callback route */}
          <Route path="/app/callback" element={<CallbackPage />} />

          {/* Protected routes */}
          <Route path="/app" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
          <Route path="/app/prompts" element={<ProtectedRoute><PromptsPage /></ProtectedRoute>} />
          <Route path="/app/content" element={<ProtectedRoute><ContentPageWrapper /></ProtectedRoute>} />
          <Route path="/app/content/:id" element={<ProtectedRoute><ContentPageWrapper /></ProtectedRoute>} />
          <Route path="/app/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
          <Route path="/app/skills" element={<ProtectedRoute><SkillsPage /></ProtectedRoute>} />
          <Route path="/app/setup" element={<ProtectedRoute><SetupPage /></ProtectedRoute>} />
          <Route path="/app/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
          <Route path="/app/admin/*" element={<AdminShell />} />

          {/* Catch all */}
          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      </main>
      <Toast show={show} message={message} />
      <SetupRequiredModal show={showSetupModal} onConfirm={handleSetupConfirm} />
      <InsufficientCreditsModal />
    </div>
  )
}

/**
 * Root router: Landing page and Admin render independently,
 * all other routes go through the appropriate inner component with sidebar layout.
 */
// Load SVG icon sprites once (available globally for all pages including admin)
function useSvgSprites() {
  useEffect(() => {
    if (document.getElementById('app-svg-sprites')) return
    const svgIcons = `
<svg style="display: none;">
  <symbol id="icon-search" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></symbol>
  <symbol id="icon-home" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></symbol>
  <symbol id="icon-chart" viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></symbol>
  <symbol id="icon-edit" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></symbol>
  <symbol id="icon-alert" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></symbol>
  <symbol id="icon-file" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></symbol>
  <symbol id="icon-plus" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></symbol>
  <symbol id="icon-check" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></symbol>
  <symbol id="icon-refresh" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></symbol>
  <symbol id="icon-link" viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></symbol>
  <symbol id="icon-lock" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></symbol>
  <symbol id="icon-chevron" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></symbol>
  <symbol id="icon-arrow-left" viewBox="0 0 24 24"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></symbol>
  <symbol id="icon-sparkles" viewBox="0 0 24 24"><path d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z"/></symbol>
  <symbol id="icon-trash" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></symbol>
  <symbol id="icon-settings" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></symbol>
  <symbol id="icon-list" viewBox="0 0 24 24"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></symbol>
  <symbol id="icon-grid" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></symbol>
  <symbol id="icon-credit-card" viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></symbol>
  <!-- Platform Icons -->
  <symbol id="icon-blog" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-width="2" d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><line fill="none" stroke="currentColor" stroke-width="2" x1="6" y1="8" x2="18" y2="8"/><line fill="none" stroke="currentColor" stroke-width="2" x1="6" y1="12" x2="18" y2="12"/><line fill="none" stroke="currentColor" stroke-width="2" x1="6" y1="16" x2="12" y2="16"/></symbol>
  <symbol id="icon-twitter" viewBox="0 0 24 24"><path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></symbol>
  <symbol id="icon-linkedin" viewBox="0 0 24 24"><path fill="currentColor" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></symbol>
  <symbol id="icon-reddit" viewBox="0 0 24 24"><path fill="currentColor" d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></symbol>
  <!-- AI Model Icons (Official) -->
  <symbol id="icon-chatgpt" viewBox="0 0 24 24"><path fill="currentColor" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997z"/></symbol>
  <symbol id="icon-claude" viewBox="0 0 24 24"><path fill="currentColor" d="M17.304 3.541l-5.327 5.198a4.124 4.124 0 0 1-5.765 0l-.537-.524a1.934 1.934 0 0 0-2.697 0l-.537.524a4.124 4.124 0 0 1-5.765 0L17.304 3.54zm0 0l1.752 1.713a5.627 5.627 0 0 1 0 8.062l-6.048 5.912a5.627 5.627 0 0 1-7.848 0l-.816-.797a1.934 1.934 0 0 0-2.697 0l-.816.797a5.627 5.627 0 0 1-7.848 0L17.304 3.54zm5.725 5.725l-5.198-5.327a5.627 5.627 0 0 0-8.062 0L3.857 9.987a1.934 1.934 0 0 0 0 2.697l.524.537a4.124 4.124 0 0 0 0 5.765l5.198 5.327a5.627 5.627 0 0 0 8.062 0l6.048-5.912a5.627 5.627 0 0 0 0-8.062l-.524-.537a1.934 1.934 0 0 0 0-2.697l-.524-.537a4.124 4.124 0 0 0-5.765 0zm-5.725 5.725l-5.198 5.327a5.627 5.627 0 0 1-8.062 0l-6.048-5.912a5.627 5.627 0 0 1 0-8.062l.524-.537a1.934 1.934 0 0 0 0-2.697l-.524-.537a4.124 4.124 0 0 1 0-5.765l5.198-5.327a5.627 5.627 0 0 1 8.062 0l6.048 5.912a5.627 5.627 0 0 1 0 8.062l-.524.537a1.934 1.934 0 0 0 0 2.697l.524.537a4.124 4.124 0 0 1 0 5.765z"/></symbol>
  <symbol id="icon-gemini" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2L2 22h20L12 2zm0 3.5L18.5 20h-13L12 5.5z"/></symbol>
  <symbol id="icon-perplexity" viewBox="0 0 24 24"><path fill="currentColor" d="M12.001 0C5.374 0 0 5.374 0 12.001c0 6.627 5.374 12.001 12.001 12.001 6.627 0 12.001-5.374 12.001-12.001C24.002 5.374 18.628 0 12.001 0zm6.199 14.087l-2.085 2.085a2.837 2.837 0 0 1-4.009 0l-2.967-2.967a2.837 2.837 0 0 1 0-4.009l2.085-2.085a2.837 2.837 0 0 1 4.009 0l2.967 2.967a2.837 2.837 0 0 1 0 4.009z"/></symbol>
</svg>
`
    const svgContainer = document.createElement('div')
    svgContainer.id = 'app-svg-sprites'
    svgContainer.innerHTML = svgIcons
    document.body.insertBefore(svgContainer, document.body.firstChild)
  }, [])
}

const AppRouter = () => {
  const location = useLocation()

  // Ensure SVG sprites are loaded for all pages (admin, frontend, etc.)
  useSvgSprites()

  // Independent layout pages (no sidebar, no auth)
  if (location.pathname === '/' || location.pathname === '/landing-page') {
    return <LandingPage />
  }
  if (location.pathname === '/blog') {
    return <BlogPage />
  }
  // Admin has its own layout (AdminShell with AdminSidebar)
  if (location.pathname.startsWith('/app/admin')) {
    return <AdminShell />
  }

  // Use appropriate inner component based on Logto configuration
  return isLogtoEnabled() ? <LogtoAppInner /> : <DevModeAppInner />
}

function App() {
  return (
    <BrowserRouter>
      <LogtoProviderWrapper>
        <LogtoTokenSetup />
        <AppRouter />
      </LogtoProviderWrapper>
    </BrowserRouter>
  )
}

export default App
